import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Delete, X, ArrowRight, Globe } from "lucide-react";
import api from "../../lib/api";

const MAX_PIN_LENGTH = 8;
const MIN_PIN_LENGTH = 4;

export default function PinLogin() {
  const { posLogin } = useAuth();
  const navigate = useNavigate();
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [shake, setShake] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lockCountdown, setLockCountdown] = useState(0);
  const [serverIp, setServerIp] = useState("");

  useEffect(() => {
    api.get("/server-ip").then(r => setServerIp(r.data.ip)).catch(() => {});
  }, []);

  const handleSubmit = useCallback(async () => {
    if (loading || lockCountdown > 0 || pin.length < MIN_PIN_LENGTH) return;
    setLoading(true);
    setError("");
    try {
      await posLogin(pin);
      navigate("/pos/room");
    } catch (err) {
      const detail = err.response?.data?.detail || "Invalid PIN";
      if (detail.startsWith("ACCOUNT_LOCKED:")) {
        const secs = parseInt(detail.split(":")[1]) || 1800;
        setLockCountdown(secs);
        setError("Account locked");
      } else {
        setError(detail);
      }
      setShake(true);
      setTimeout(() => setShake(false), 500);
      setPin("");
    } finally {
      setLoading(false);
    }
  }, [loading, lockCountdown, posLogin, navigate, pin]);

  // Lock countdown
  useEffect(() => {
    if (lockCountdown <= 0) return;
    const iv = setInterval(() => setLockCountdown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(iv);
  }, [lockCountdown]);

  const addDigit = (d) => {
    if (pin.length < MAX_PIN_LENGTH && lockCountdown <= 0) setPin((p) => p + d);
  };
  const backspace = () => setPin((p) => p.slice(0, -1));
  const clear = () => setPin("");

  const formatCountdown = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  return (
    <div className="min-h-[100dvh] flex flex-col items-center justify-center bg-white" data-testid="pos-login-page">
      <div className="w-full max-w-xs flex flex-col items-center px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-heading text-2xl font-black tracking-tighter">MINIBAR</h1>
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gray-400 mt-1">Point of Sale</p>
        </div>

        {/* PIN Dots — supports 4-8 digits */}
        <div className={`flex gap-3 mb-2 ${shake ? "animate-shake" : ""}`} data-testid="pin-dots">
          {Array.from({ length: MAX_PIN_LENGTH }).map((_, i) => (
            <div key={`dot-${i}`} className={`w-3.5 h-3.5 rounded-full border-2 transition-all duration-150 ${
              i < pin.length ? "bg-black border-black scale-110" : i < MIN_PIN_LENGTH ? "border-gray-300" : "border-gray-200 border-dashed"
            }`} />
          ))}
        </div>
        <p className="text-[10px] text-gray-400 mb-4">{pin.length} of {MIN_PIN_LENGTH}–{MAX_PIN_LENGTH} digits</p>

        {/* Error / Lock */}
        {error && !lockCountdown && (
          <p className="text-red-500 text-sm font-medium mb-4" data-testid="pin-error">{error}</p>
        )}
        {lockCountdown > 0 && (
          <div className="text-center mb-4" data-testid="lock-countdown">
            <p className="text-red-500 text-sm font-medium">Account locked</p>
            <p className="font-heading text-2xl font-black text-red-500 mt-1">{formatCountdown(lockCountdown)}</p>
          </div>
        )}

        {/* Keypad */}
        <div className="grid grid-cols-3 gap-3 w-full max-w-[280px]" data-testid="pin-keypad">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
            <button key={d} onClick={() => addDigit(String(d))} disabled={lockCountdown > 0}
              className="keypad-btn rounded-none" data-testid={`pin-keypad-${d}`}>
              {d}
            </button>
          ))}
          <button onClick={clear} className="keypad-btn rounded-none text-gray-400" data-testid="pin-keypad-clear">
            <X className="w-5 h-5" />
          </button>
          <button onClick={() => addDigit("0")} disabled={lockCountdown > 0}
            className="keypad-btn rounded-none" data-testid="pin-keypad-0">0</button>
          <button onClick={backspace} className="keypad-btn rounded-none text-gray-400" data-testid="pin-keypad-backspace">
            <Delete className="w-5 h-5" />
          </button>
        </div>

        {/* Submit Button — visible when PIN has minimum digits */}
        {pin.length >= MIN_PIN_LENGTH && (
          <button onClick={handleSubmit} disabled={loading}
            className="mt-4 w-full max-w-[280px] h-12 bg-black text-white font-bold text-sm flex items-center justify-center gap-2 active:bg-gray-800 animate-fade-in"
            data-testid="pin-submit-button">
            {loading ? "Verifying..." : <><span>Sign In</span><ArrowRight className="w-4 h-4" /></>}
          </button>
        )}

        {loading && !pin.length && <p className="mt-4 text-sm text-gray-400">Verifying...</p>}

        <div className="mt-8 flex flex-col items-center gap-2">
          <a href="/admin/login" className="text-[10px] text-gray-300 uppercase tracking-[0.15em] hover:text-gray-500 transition-colors">
            Admin Portal
          </a>
          {serverIp && (
            <span className="flex items-center gap-1 text-[10px] text-gray-300">
              <Globe className="w-3 h-3" /> Server IP: {serverIp}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
